// Feature: features/nma-appeal-letter-generator.md, features/nma-appeal-templates-demo-ui.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Flex,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Select,
  Input,
  Button,
  Checkbox,
  Code,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  HStack,
  VStack,
  Divider,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import {
  RepeatIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
  AddIcon,
  CopyIcon,
  DownloadIcon,
  AttachmentIcon,
} from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { formatCurrency } from './NMAClaims/shared';
import {
  loadAppealTemplates,
  saveAppealTemplates,
  resetAppealTemplatesToDefault,
  loadClaimAppealSelections,
  saveClaimAppealSelection,
  matchTemplatesForClaim,
} from './NMAClaims/appealTemplatesStore';
import NMAAppealTemplatesTab from './NMAClaims/NMAAppealTemplatesTab';
import NMAAppealLaunchModal from './NMAClaims/NMAAppealLaunchModal';

const normalizeMarkdownText = (rawText) => {
  if (!rawText) return '';
  return String(rawText)
    .replace(/\$?\\rightarrow\$?/g, '->')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '    ');
};

const AppealMarkdownBox = ({ text, bg = 'white', p = 5 }) => {
  const normalized = normalizeMarkdownText(text);
  if (!normalized) return null;
  return (
    <Box
      bg={bg}
      p={p}
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      fontSize="sm"
      color="gray.800"
      lineHeight="1.65"
      sx={{
        '& p': { mb: 3, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } },
        '& ul': { listStyleType: 'disc', pl: 6, mb: 3 },
        '& ol': { listStyleType: 'decimal', pl: 6, mb: 3 },
        '& li': { mb: 1.5, whiteSpace: 'pre-wrap' },
        '& h1': { fontSize: 'lg', fontWeight: 'bold', mt: 3, mb: 2, borderBottom: '1px solid #E2E8F0', pb: 1 },
        '& h2': { fontSize: 'md', fontWeight: 'bold', mt: 3, mb: 2 },
        '& h3, & h4': { fontSize: 'sm', fontWeight: 'bold', mt: 2.5, mb: 1.5 },
        '& strong, & b': { fontWeight: 'bold' },
        '& blockquote': {
          borderLeft: '3px solid #3182CE',
          pl: 3,
          py: 1,
          my: 2,
          bg: 'blue.50',
          fontStyle: 'italic',
        },
        '& table': { width: '100%', borderCollapse: 'collapse', my: 3, fontSize: 'xs' },
        '& th, & td': { border: '1px solid #CBD5E0', px: 2, py: 1.5, textAlign: 'left' },
        '& th': { bg: 'gray.100', fontWeight: 'bold' },
        '& code': {
          bg: 'gray.100',
          px: 1,
          py: 0.5,
          borderRadius: 'sm',
          fontSize: '0.9em',
          fontFamily: 'mono',
        },
      }}
    >
      <ReactMarkdown>{normalized}</ReactMarkdown>
    </Box>
  );
};

const getAppealStatusBadge = (status) => {
  switch (status) {
    case 'APPEAL_READY':
      return <Badge colorScheme="green" px={2} py={0.5}>Appeal Letter Ready</Badge>;
    case 'JOB_PROCESSING':
      return (
        <Badge colorScheme="purple" px={2} py={0.5}>
          <HStack spacing={1.5} display="inline-flex" alignItems="center">
            <Spinner size="xs" speed="0.8s" />
            <Text as="span">Generating Appeal...</Text>
          </HStack>
        </Badge>
      );
    case 'JOB_PENDING':
      return <Badge colorScheme="blue" px={2} py={0.5}>Job Queued (Pending)</Badge>;
    case 'JOB_FAILED':
      return <Badge colorScheme="red" px={2} py={0.5}>Job Failed</Badge>;
    default:
      return <Badge colorScheme="orange" variant="subtle" px={2} py={0.5}>Waiting on Appeal</Badge>;
  }
};

const NMAAppealsDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Tab index: 0 = Appeals Queue & Generator, 1 = Appeal Templates & Policy Library
  const [activeTab, setActiveTab] = useState(0);

  // Client-side templates library & per-claim template selections
  const [templates, setTemplates] = useState(() => loadAppealTemplates());
  const [claimSelections, setClaimSelections] = useState(() => loadClaimAppealSelections());

  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingClaimMap, setCreatingClaimMap] = useState({});

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayer, setSelectedPayer] = useState('');
  const [selectedClaimType, setSelectedClaimType] = useState('');
  const [selectedAppealStatus, setSelectedAppealStatus] = useState('');
  const [onlyWithDenialDoc, setOnlyWithDenialDoc] = useState(false);

  // Appeal Letter Viewer Modal
  const { isOpen: isLetterOpen, onOpen: onOpenLetter, onClose: onCloseLetter } = useDisclosure();
  const [selectedClaimRow, setSelectedClaimRow] = useState(null);

  // Pre-Flight Template Review & Launch Modal
  const {
    isOpen: isLaunchOpen,
    onOpen: onOpenLaunch,
    onClose: onCloseLaunch,
  } = useDisclosure();
  const [launchClaimRow, setLaunchClaimRow] = useState(null);
  const [launchForceRegenerate, setLaunchForceRegenerate] = useState(false);

  useEffect(() => {
    const qs = new URLSearchParams(location.search || '');
    const tabParam = (qs.get('tab') || '').trim().toLowerCase();
    if (tabParam === 'templates' || tabParam === 'library' || tabParam === '1') {
      setActiveTab(1);
    } else if (tabParam === 'queue' || tabParam === '0') {
      setActiveTab(0);
    }
  }, [location.search]);

  const handleSaveTemplates = useCallback((nextTemplates) => {
    setTemplates(nextTemplates);
    saveAppealTemplates(nextTemplates);
  }, []);

  const handleResetTemplates = useCallback(() => {
    const defaults = resetAppealTemplatesToDefault();
    setTemplates(defaults);
  }, []);

  const fetchAppealsQueue = useCallback(async (showSpinner = true) => {
    if (!accessToken) return;
    if (showSpinner) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedPayer) params.append('payer', selectedPayer);
      if (selectedClaimType) params.append('claim_type', selectedClaimType);
      if (selectedAppealStatus) params.append('appeal_status', selectedAppealStatus);
      if (onlyWithDenialDoc) params.append('only_with_denial_doc', 'true');
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('limit', '250');

      const res = await callApi(`/api/nma/appeals/queue?${params.toString()}`, accessToken);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load NMA appeals queue');
      }
      const data = await res.json();
      setQueueData(data);
    } catch (error) {
      console.error('Error fetching NMA appeals queue:', error);
      toast({
        title: 'Error loading appeals queue',
        description: error.message,
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [accessToken, selectedPayer, selectedClaimType, selectedAppealStatus, onlyWithDenialDoc, searchQuery, toast]);

  useEffect(() => {
    fetchAppealsQueue(true);
  }, [fetchAppealsQueue]);

  // Auto-poll every 15 seconds if there are active pending/processing appeal jobs
  useEffect(() => {
    const activeCnt = queueData?.summary?.active_jobs_count || 0;
    if (!accessToken || activeCnt === 0) return undefined;
    const interval = setInterval(() => {
      fetchAppealsQueue(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [accessToken, queueData?.summary?.active_jobs_count, fetchAppealsQueue]);

  const handleOpenLaunchModal = (claimRow, force = false) => {
    setLaunchClaimRow(claimRow);
    setLaunchForceRegenerate(force);
    onOpenLaunch();
  };

  const handleConfirmLaunchAppeal = async ({
    claimRow,
    customInstructions,
    selectionMeta,
    force = false,
  }) => {
    const claimNum = claimRow.claim_number;
    setCreatingClaimMap((prev) => ({ ...prev, [claimNum]: true }));
    try {
      const updatedSelections = saveClaimAppealSelection(claimNum, selectionMeta);
      setClaimSelections(updatedSelections);

      const res = await callApi('/api/nma/appeals/jobs', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_number: claimNum,
          case_id: claimRow.case_id,
          force,
          custom_instructions: customInstructions,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Failed to queue appeal job for ${claimNum}`);
      }
      toast({
        title: data.already_queued
          ? 'Appeal Job Already Active'
          : selectionMeta?.submission_channel === 'PAYER_PORTAL_EAPPEAL'
          ? 'eAppeal Job Queued with Selected Templates'
          : 'Appeal Letter Job Created with Selected Templates',
        description:
          data.message ||
          `Queued nma_appeal_letter job for ${claimNum} using ${(selectionMeta?.template_names || []).length} template(s).`,
        status: data.already_queued ? 'info' : 'success',
        duration: 4000,
        isClosable: true,
      });
      onCloseLaunch();
      await fetchAppealsQueue(false);
    } catch (error) {
      toast({
        title: 'Error creating appeal job',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingClaimMap((prev) => ({ ...prev, [claimNum]: false }));
    }
  };

  const handleOpenLetterModal = (claimRow) => {
    setSelectedClaimRow(claimRow);
    onOpenLetter();
  };

  const handleCopyMarkdown = (mdText) => {
    if (!mdText) return;
    navigator.clipboard.writeText(normalizeMarkdownText(mdText));
    toast({
      title: 'Copied to clipboard',
      description: 'Appeal letter Markdown copied to clipboard.',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleDownloadMarkdown = (claimNum, mdText) => {
    if (!mdText) return;
    const blob = new Blob([normalizeMarkdownText(mdText)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NMA_Appeal_Letter_${claimNum}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const summary = queueData?.summary || {};
  const claims = queueData?.claims || [];
  const payers = queueData?.filter_options?.payers || [];

  const activeAnalysis = selectedClaimRow?.appeal_analysis?.analysis_result || {};
  const letterMarkdown = activeAnalysis.report_markdown || '';
  const appealMeta = activeAnalysis.appeal_metadata || activeAnalysis.header_block || {};

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />

      <Box maxW="1650px" mx="auto" px={6} py={6}>
        {/* Top Header & Navigation */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={5}>
          <Box>
            <HStack spacing={3} mb={1}>
              <Heading size="lg" color="gray.800">
                NMA Appeal Letter Queue, Templates &amp; Generator
              </Heading>
              <Badge colorScheme="purple" fontSize="xs" px={2.5} py={1} borderRadius="md">
                job_type: nma_appeal_letter
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.600">
              Manage payer &amp; procedure appeal templates, review auto-matched templates before launching an appeal or eAppeal, and export clinical appeal packets.
            </Text>
          </Box>

          <HStack spacing={2.5} wrap="wrap">
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => navigate('/nma-claims')}
            >
              NMA Claims Dashboard
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={() => navigate('/nma-meta-analysis')}
            >
              Meta-Analysis Report
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              leftIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/jobs')}
            >
              Jobs Queue (/jobs)
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<RepeatIcon />}
              isLoading={loading}
              onClick={() => fetchAppealsQueue(true)}
            >
              Refresh Queue
            </Button>
          </HStack>
        </Flex>

        <Tabs
          index={activeTab}
          onChange={setActiveTab}
          colorScheme="blue"
          variant="enclosed"
          bg="white"
          borderRadius="lg"
          shadow="sm"
          p={4}
        >
          <TabList mb={4} flexWrap="wrap">
            <Tab fontWeight="bold">
              1. Appeals Queue &amp; Generator ({claims.length})
            </Tab>
            <Tab fontWeight="bold" color="purple.700">
              2. Appeal Templates &amp; Policy Library ({templates.length})
            </Tab>
          </TabList>

          <TabPanels>
            {/* TAB 1: APPEALS QUEUE & GENERATOR */}
            <TabPanel px={0} py={0}>
              {/* Summary KPI Strip */}
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={5}>
                <Card variant="outline" bg="white" borderTop="3px solid #DD6B20">
                  <CardBody py={3.5} px={4}>
                    <Stat>
                      <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                        Denied Claims Eligible for Appeal
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="orange.600">
                        {summary.total_eligible_claims ?? 0}
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">
                        Showing {summary.filtered_claims_count ?? claims.length} matching filters (excl. 95999-only)
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>

                <Card variant="outline" bg="white" borderTop="3px solid #E53E3E">
                  <CardBody py={3.5} px={4}>
                    <Stat>
                      <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                        With Lockbox Denial Letter
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="red.600">
                        {summary.with_denial_letter_count ?? 0}
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">
                        Extracted INITIAL_DENIAL_LETTER / APPEAL_DENIAL
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>

                <Card variant="outline" bg="white" borderTop="3px solid #3182CE">
                  <CardBody py={3.5} px={4}>
                    <Stat>
                      <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                        Active Appeal Jobs (Pending / Processing)
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="blue.600">
                        {summary.active_jobs_count ?? 0}
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">
                        Worker: --integration nma_appeal_letter_generator
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>

                <Card variant="outline" bg="white" borderTop="3px solid #38A169">
                  <CardBody py={3.5} px={4}>
                    <Stat>
                      <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                        Appeal Letters Ready
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="green.600">
                        {summary.drafts_ready_count ?? 0}
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">
                        Stored in payer_data_analysis (claim_appeal_letter)
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Filter Bar */}
              <Card variant="outline" bg="white" mb={5}>
                <CardBody py={3} px={4}>
                  <Flex gap={3} wrap="wrap" align="center">
                    <Box flex="1" minW="240px">
                      <Input
                        size="sm"
                        placeholder="Search claim # (e.g. P295112), case #, patient, payer, CPT, or CARC..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Box>

                    <Box minW="220px">
                      <Select
                        size="sm"
                        placeholder="All Payers"
                        value={selectedPayer}
                        onChange={(e) => setSelectedPayer(e.target.value)}
                      >
                        {payers.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </Select>
                    </Box>

                    <Box minW="150px">
                      <Select
                        size="sm"
                        placeholder="All Claim Types"
                        value={selectedClaimType}
                        onChange={(e) => setSelectedClaimType(e.target.value)}
                      >
                        <option value="pro">Professional (Pro)</option>
                        <option value="tech">Technical (Tech)</option>
                      </Select>
                    </Box>

                    <Box minW="185px">
                      <Select
                        size="sm"
                        placeholder="All Appeal Statuses"
                        value={selectedAppealStatus}
                        onChange={(e) => setSelectedAppealStatus(e.target.value)}
                      >
                        <option value="NEEDS_APPEAL">Waiting on Appeal</option>
                        <option value="APPEAL_READY">Appeal Letter Ready</option>
                        <option value="JOB_PENDING">Job Queued (Pending)</option>
                        <option value="JOB_PROCESSING">Generating Appeal</option>
                        <option value="JOB_FAILED">Job Failed</option>
                      </Select>
                    </Box>

                    <Checkbox
                      size="sm"
                      colorScheme="red"
                      isChecked={onlyWithDenialDoc}
                      onChange={(e) => setOnlyWithDenialDoc(e.target.checked)}
                    >
                      Has Denial Letter Doc Only
                    </Checkbox>

                    {(searchQuery || selectedPayer || selectedClaimType || selectedAppealStatus || onlyWithDenialDoc) && (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedPayer('');
                          setSelectedClaimType('');
                          setSelectedAppealStatus('');
                          setOnlyWithDenialDoc(false);
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Flex>
                </CardBody>
              </Card>

              {/* Main Claims Table */}
              <Card variant="outline" bg="white">
                <CardHeader py={3} px={4} borderBottom="1px solid" borderColor="gray.100">
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Heading size="sm" color="gray.700">
                      Claims Waiting on Appeal Letters ({claims.length})
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      Click &ldquo;Review Templates &amp; Appeal&rdquo; on any claim to inspect/change matched templates before queueing
                    </Text>
                  </Flex>
                </CardHeader>

                <CardBody p={0}>
                  {loading ? (
                    <Flex justify="center" align="center" py={16}>
                      <Spinner size="lg" color="blue.500" mr={3} />
                      <Text color="gray.600">Loading denied claims &amp; appeal statuses...</Text>
                    </Flex>
                  ) : claims.length === 0 ? (
                    <Box py={12} textAlign="center">
                      <Text color="gray.500" fontSize="sm">
                        No denied claims matched your current filters.
                      </Text>
                    </Box>
                  ) : (
                    <TableContainer maxH="720px" overflowY="auto">
                      <Table size="sm" variant="simple">
                        <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                          <Tr>
                            <Th>Claim / Case</Th>
                            <Th>DOS / POS</Th>
                            <Th>Payer &amp; Facility</Th>
                            <Th>Denied Core CPTs &amp; CARC</Th>
                            <Th>Procedure &amp; Matched Templates</Th>
                            <Th>Recent Denial Doc &amp; Reason</Th>
                            <Th isNumeric>Core Denied (beta)</Th>
                            <Th>Appeal Status</Th>
                            <Th textAlign="right">Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {claims.map((row) => {
                            const isCreating = Boolean(creatingClaimMap[row.claim_number]);
                            const hasActiveJob =
                              row.appeal_status === 'JOB_PENDING' || row.appeal_status === 'JOB_PROCESSING';
                            const hasDraft = Boolean(row.appeal_analysis);
                            const denialDoc = row.recent_denial_doc || {};

                            const savedSel = claimSelections[String(row.claim_number || '').toUpperCase()];
                            const matchInfo = matchTemplatesForClaim(
                              row,
                              templates,
                              savedSel?.procedure_type || ''
                            );
                            const primaryMatched =
                              matchInfo.matchedTemplates.find((t) => !t.is_base_template) ||
                              matchInfo.matchedTemplates[0];
                            const totalFilesCnt = matchInfo.matchedTemplates.reduce(
                              (acc, t) => acc + (t.attached_files?.length || 0),
                              0
                            );

                            return (
                              <Tr
                                key={row.claim_number}
                                _hover={{ bg: 'gray.50' }}
                                bg={hasDraft ? 'green.50' : hasActiveJob ? 'blue.50' : undefined}
                              >
                                {/* Claim / Case */}
                                <Td>
                                  <HStack spacing={1.5}>
                                    <Text
                                      fontWeight="bold"
                                      color="blue.700"
                                      fontSize="xs"
                                      cursor="pointer"
                                      textDecoration="underline"
                                      onClick={() => navigate(`/nma-appeals/${encodeURIComponent(row.claim_number)}`)}
                                    >
                                      {row.claim_number}
                                    </Text>
                                    <Badge
                                      colorScheme={row.claim_type === 'pro' ? 'blue' : 'purple'}
                                      fontSize="10px"
                                    >
                                      {(row.claim_type || 'pro').toUpperCase()}
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="xs" color="gray.600">
                                    Case #{row.nma_case_no} {row.patient_name ? `• ${row.patient_name}` : ''}
                                  </Text>
                                </Td>

                                {/* DOS / POS */}
                                <Td>
                                  <Text fontSize="xs" fontWeight="medium">
                                    {row.date_of_service || 'N/A'}
                                  </Text>
                                  <Badge variant="outline" colorScheme="gray" fontSize="10px">
                                    POS {row.place_of_service || 'UNK'}
                                  </Badge>
                                </Td>

                                {/* Payer & Facility */}
                                <Td maxW="220px">
                                  <Text fontSize="xs" fontWeight="bold" color="gray.800" isTruncated title={row.payer_name}>
                                    {row.payer_name}
                                  </Text>
                                  <Text fontSize="11px" color="gray.600" isTruncated title={row.facility_name || ''}>
                                    {row.facility_name || row.rendering_provider || '—'}
                                  </Text>
                                  {row.payer_claim_control_number && (
                                    <Text fontSize="10px" color="gray.500">
                                      ICN: {row.payer_claim_control_number}
                                    </Text>
                                  )}
                                </Td>

                                {/* Denied Core CPTs & CARC */}
                                <Td maxW="210px">
                                  <HStack spacing={1} wrap="wrap" mb={1}>
                                    {(row.denied_core_cpts && row.denied_core_cpts.length > 0
                                      ? row.denied_core_cpts
                                      : row.denied_cpts || []
                                    ).map((cpt) => (
                                      <Badge key={cpt} colorScheme="red" variant="solid" fontSize="10px">
                                        {cpt}
                                      </Badge>
                                    ))}
                                    {row.top_carc && (
                                      <Badge colorScheme="orange" variant="subtle" fontSize="10px">
                                        {row.top_carc}
                                        {row.top_rarc ? ` / ${row.top_rarc}` : ''}
                                      </Badge>
                                    )}
                                  </HStack>
                                  {row.paid_cpts && row.paid_cpts.length > 0 && (
                                    <Text fontSize="10px" color="green.700">
                                      Paid: {row.paid_cpts.join(', ')}
                                    </Text>
                                  )}
                                </Td>

                                {/* Procedure & Matched Templates */}
                                <Td maxW="250px">
                                  <HStack spacing={1} wrap="wrap" mb={1}>
                                    <ChakraTooltip label={matchInfo.inferredProcedure.reason} hasArrow placement="top">
                                      <Badge
                                        colorScheme={matchInfo.inferredProcedure.colorScheme || 'blue'}
                                        fontSize="10px"
                                      >
                                        {savedSel?.procedure_type
                                          ? savedSel.procedure_type.split(' (')[0]
                                          : matchInfo.inferredProcedure.shortLabel}
                                      </Badge>
                                    </ChakraTooltip>
                                    {savedSel && (
                                      <Badge colorScheme="purple" variant="solid" fontSize="9px">
                                        Customized
                                      </Badge>
                                    )}
                                  </HStack>
                                  {primaryMatched && (
                                    <Box
                                      cursor="pointer"
                                      onClick={() => handleOpenLaunchModal(row, hasDraft)}
                                      _hover={{ textDecoration: 'underline' }}
                                    >
                                      <Text fontSize="11px" fontWeight="semibold" color="blue.700" noOfLines={1}>
                                        {primaryMatched.name}
                                      </Text>
                                      <HStack spacing={1.5} mt={0.5}>
                                        <Badge colorScheme="green" variant="subtle" fontSize="9px">
                                          {matchInfo.matchedTemplates.length} Template(s)
                                        </Badge>
                                        {totalFilesCnt > 0 && (
                                          <Badge colorScheme="gray" fontSize="9px">
                                            <AttachmentIcon mr={0.5} boxSize={2} />
                                            {totalFilesCnt} File(s)
                                          </Badge>
                                        )}
                                      </HStack>
                                    </Box>
                                  )}
                                </Td>

                                {/* Recent Denial Doc & Reason */}
                                <Td maxW="280px">
                                  <HStack spacing={1.5} mb={0.5} wrap="wrap">
                                    {denialDoc.has_denial_letter ? (
                                      <Badge colorScheme="red" variant="outline" fontSize="10px">
                                        {denialDoc.doc_type || 'DENIAL_LETTER'}
                                        {denialDoc.doc_date ? ` (${denialDoc.doc_date})` : ''}
                                      </Badge>
                                    ) : (
                                      <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                                        EOB {denialDoc.doc_date ? `(${denialDoc.doc_date})` : 'Denial'}
                                      </Badge>
                                    )}
                                    {denialDoc.response_deadline_date && (
                                      <Badge colorScheme="purple" fontSize="10px">
                                        Due: {denialDoc.response_deadline_date}
                                      </Badge>
                                    )}
                                  </HStack>
                                  <ChakraTooltip label={row.primary_denial_reason} hasArrow placement="top">
                                    <Text fontSize="11px" color="gray.700" noOfLines={2}>
                                      {row.primary_denial_reason || '—'}
                                    </Text>
                                  </ChakraTooltip>
                                </Td>

                                {/* Core Denied Amount */}
                                <Td isNumeric>
                                  <Text fontSize="xs" fontWeight="bold" color="red.600">
                                    {formatCurrency(row.core_denied_amount)}
                                  </Text>
                                  {row.total_paid > 0 && (
                                    <Text fontSize="10px" color="green.600">
                                      Paid: {formatCurrency(row.total_paid)}
                                    </Text>
                                  )}
                                </Td>

                                {/* Appeal Status */}
                                <Td>
                                  {getAppealStatusBadge(row.appeal_status)}
                                  {row.appeal_analysis?.created_at && (
                                    <Text fontSize="10px" color="gray.500" mt={0.5}>
                                      Generated {new Date(row.appeal_analysis.created_at).toLocaleDateString()}
                                    </Text>
                                  )}
                                </Td>

                                {/* Actions */}
                                <Td textAlign="right">
                                  <HStack spacing={1.5} justify="flex-end">
                                    {hasDraft && (
                                      <Button
                                        size="xs"
                                        colorScheme="green"
                                        leftIcon={<CheckCircleIcon />}
                                        onClick={() => navigate(`/nma-appeals/${encodeURIComponent(row.claim_number)}`)}
                                      >
                                        View Appeal &amp; PDF
                                      </Button>
                                    )}

                                    <Button
                                      size="xs"
                                      colorScheme={hasDraft ? 'gray' : 'blue'}
                                      variant={hasDraft ? 'outline' : 'solid'}
                                      leftIcon={<AddIcon />}
                                      isLoading={isCreating}
                                      isDisabled={hasActiveJob}
                                      onClick={() => handleOpenLaunchModal(row, hasDraft)}
                                    >
                                      {hasActiveJob
                                        ? 'Job Active'
                                        : hasDraft
                                        ? 'Regenerate'
                                        : 'Review Templates & Appeal'}
                                    </Button>

                                    <ChakraTooltip label="Inspect full claim timeline in /nma-claims Explorer">
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        onClick={() =>
                                          navigate(`/nma-claims?tab=explorer&search=${encodeURIComponent(row.claim_number)}`)
                                        }
                                      >
                                        <ExternalLinkIcon />
                                      </Button>
                                    </ChakraTooltip>
                                  </HStack>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            </TabPanel>

            {/* TAB 2: APPEAL TEMPLATES & POLICY LIBRARY */}
            <TabPanel px={0} py={0}>
              <NMAAppealTemplatesTab
                templates={templates}
                onSaveTemplates={handleSaveTemplates}
                onResetDefaults={handleResetTemplates}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Pre-Flight Template Review & Launch Modal */}
      <NMAAppealLaunchModal
        isOpen={isLaunchOpen}
        onClose={onCloseLaunch}
        claimRow={launchClaimRow}
        templates={templates}
        savedSelection={
          launchClaimRow
            ? claimSelections[String(launchClaimRow.claim_number || '').toUpperCase()]
            : null
        }
        isSubmitting={
          launchClaimRow ? Boolean(creatingClaimMap[launchClaimRow.claim_number]) : false
        }
        forceRegenerate={launchForceRegenerate}
        onConfirmLaunch={handleConfirmLaunchAppeal}
        onSwitchToTemplatesTab={() => setActiveTab(1)}
      />

      {/* Appeal Letter Viewer Modal */}
      <Modal isOpen={isLetterOpen} onClose={onCloseLetter} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={3}>
            <Flex justify="space-between" align="center" pr={8} wrap="wrap" gap={2}>
              <Box>
                <HStack spacing={2.5}>
                  <Text fontWeight="bold" fontSize="lg">
                    Appeal Letter: {selectedClaimRow?.claim_number}
                  </Text>
                  <Badge colorScheme="blue">{selectedClaimRow?.payer_name}</Badge>
                  {activeAnalysis.appeal_pathway && (
                    <Badge colorScheme="purple">{activeAnalysis.appeal_pathway}</Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.600" fontWeight="normal" mt={1}>
                  Case #{selectedClaimRow?.nma_case_no} • DOS: {selectedClaimRow?.date_of_service || 'N/A'} • Denied Core CPTs:{' '}
                  {(selectedClaimRow?.denied_core_cpts || []).join(', ') || 'N/A'}
                </Text>
              </Box>

              <HStack spacing={2}>
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  leftIcon={<CopyIcon />}
                  onClick={() => handleCopyMarkdown(letterMarkdown)}
                  isDisabled={!letterMarkdown}
                >
                  Copy Letter
                </Button>
                <Button
                  size="xs"
                  colorScheme="green"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownloadMarkdown(selectedClaimRow?.claim_number || 'claim', letterMarkdown)}
                  isDisabled={!letterMarkdown}
                >
                  Download .md
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4} bg="gray.50">
            {!selectedClaimRow?.appeal_analysis ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                No completed appeal letter found for this claim yet.
              </Alert>
            ) : (
              <VStack align="stretch" spacing={4}>
                {/* Headline & Strategy Summary */}
                {(activeAnalysis.headline || activeAnalysis.root_cause_summary || activeAnalysis.recommendation) && (
                  <Card variant="outline" bg="white" borderLeft="4px solid #3182CE">
                    <CardBody py={3} px={4}>
                      {activeAnalysis.headline && (
                        <Heading size="xs" color="blue.800" mb={2}>
                          {activeAnalysis.headline}
                        </Heading>
                      )}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {activeAnalysis.root_cause_summary && (
                          <Box>
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                              Denial Root Cause & Clinical Basis
                            </Text>
                            <AppealMarkdownBox text={activeAnalysis.root_cause_summary} bg="gray.50" p={3} />
                          </Box>
                        )}
                        {activeAnalysis.recommendation && (
                          <Box>
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                              Submission & Enclosures Checklist
                            </Text>
                            <AppealMarkdownBox text={activeAnalysis.recommendation} bg="blue.50" p={3} />
                          </Box>
                        )}
                      </SimpleGrid>

                      {/* Optional Destination / ICN Metadata */}
                      {(appealMeta.submission_destination ||
                        appealMeta.appeals_destination ||
                        selectedClaimRow?.recent_denial_doc?.submission_destination) && (
                        <Box mt={3} pt={2} borderTop="1px solid" borderColor="gray.100">
                          <Text fontSize="xs" color="gray.700">
                            <strong>Submission Destination:</strong>{' '}
                            <Code fontSize="xs">
                              {appealMeta.submission_destination ||
                                appealMeta.appeals_destination ||
                                selectedClaimRow?.recent_denial_doc?.submission_destination}
                            </Code>
                          </Text>
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                )}

                <Divider />

                {/* Full Appeal Letter Markdown */}
                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Heading size="xs" color="gray.700" textTransform="uppercase">
                      Draft Appeal Letter (Markdown)
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      Analysis ID #{selectedClaimRow.appeal_analysis.id}
                    </Text>
                  </Flex>
                  <AppealMarkdownBox text={letterMarkdown} bg="white" p={6} />
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.200">
            <HStack spacing={2}>
              {selectedClaimRow && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<RepeatIcon />}
                  isLoading={Boolean(creatingClaimMap[selectedClaimRow.claim_number])}
                  onClick={() => {
                    onCloseLetter();
                    handleOpenLaunchModal(selectedClaimRow, true);
                  }}
                >
                  Review Templates &amp; Regenerate
                </Button>
              )}
              <Button size="sm" onClick={onCloseLetter}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default NMAAppealsDashboard;
